import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ImageBackground,
  Linking,
  AppState,
  AppStateStatus,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { router, useLocalSearchParams } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';

export default function EnableNotificationsScreen() {
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const { sponsorName } = useLocalSearchParams<{ sponsorName: string }>();
  const appState = useRef(AppState.currentState);

  const handleBackToProfile = () => {
    if (router.canDismiss()) {
      router.dismissAll();
    }
    router.replace('/diary');
    setTimeout(() => {
      router.push('/perfil');
    }, 100);
  };

  const handleOpenSettings = async () => {
    try {
      if (Platform.OS === 'ios') {
        await Linking.openURL('app-settings:');
      } else {
        await Linking.openSettings();
      }
    } catch (error) {
      console.error('Error opening settings:', error);
      await Linking.openSettings();
    }
  };

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        console.log('📱 App returned from background, navigating to profile');
        handleBackToProfile();
      }
      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, []);

  return (
    <ImageBackground
      source={require('@/assets/images/onboarding-push.png')}
      style={styles.fullScreenBackground}
      resizeMode="cover"
    >
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>{t('sponsors.enableNotifications.header')}</Text>
        </View>

        <View style={styles.content}>
          <View style={styles.textSection}>
            <Text style={styles.title}>
              {t('sponsors.enableNotifications.title')}
            </Text>
            <Text style={styles.subtitle}>
              {t('sponsors.enableNotifications.subtitle', { sponsorName: sponsorName || '' })}
            </Text>
          </View>
        </View>

        <View style={[styles.buttonSection, { paddingBottom: insets.bottom + 24 }]}>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleOpenSettings}
            activeOpacity={0.8}
          >
            <Text style={styles.primaryButtonText}>
              {t('sponsors.enableNotifications.settingsButton')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.tertiaryButton}
            onPress={handleBackToProfile}
            activeOpacity={0.8}
          >
            <Text style={styles.tertiaryButtonText}>
              {t('sponsors.enableNotifications.skipButton')}
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  fullScreenBackground: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
  },
  headerTitle: {
    fontSize: 14,
    color: '#151515',
    fontFamily: 'Inter_700Bold',
    lineHeight: 14,
  },
  content: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  textSection: {
    alignItems: 'center',
    gap: 24,
    marginBottom: 24,
  },
  title: {
    color: '#015480',
    textAlign: 'center',
    fontFamily: 'InstrumentSerif_400Regular',
    fontSize: 40,
    fontWeight: '400',
    lineHeight: 44,
    width: 345,
  },
  subtitle: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
    width: 345,
  },
  buttonSection: {
    width: '100%',
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  primaryButton: {
    backgroundColor: '#151515',
    borderRadius: 12,
    padding: 16,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
  tertiaryButton: {
    padding: 16,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tertiaryButtonText: {
    color: '#151515',
    textAlign: 'center',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
  },
});
